<template>
  <footer class="bg-white border-t border-gray-100 pt-14 pb-10 px-6 sm:px-8 xl:px-12">
    <div class="max-w-[1200px] mx-auto">
      <div
        class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-x-10 gap-y-10 lg:gap-x-12 xl:gap-y-12 mb-12 xl:mb-14"
      >
        <!-- Brand -->
        <div class="md:col-span-2 xl:col-span-2">
          <img
            src="@/assets/tracktalent_logo.svg"
            alt="TrackTalent"
            class="h-10 sm:h-12 mb-4 opacity-90"
          />
          <p class="text-sm text-gray-500 leading-relaxed max-w-[280px] mb-6">
            Track applications and discover curated roles—built by Outtalent for ambitious engineers.
          </p>
          <div class="flex flex-wrap gap-3">
            <a
              href="https://www.instagram.com/teamouttalent/"
              class="social-btn"
              aria-label="Instagram"
              target="_blank"
              rel="noopener noreferrer"
            >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><!-- Icon from Material Design Icons by Pictogrammers - https://github.com/Templarian/MaterialDesign/blob/master/LICENSE --><path fill="currentColor" d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4zm9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8A1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5a5 5 0 0 1-5 5a5 5 0 0 1-5-5a5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3a3 3 0 0 0 3 3a3 3 0 0 0 3-3a3 3 0 0 0-3-3"/></svg>
            </a>
            <a
              href="https://www.linkedin.com/school/outtalent/"
              class="social-btn"
              aria-label="LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
            >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><!-- Icon from Material Design Icons by Pictogrammers - https://github.com/Templarian/MaterialDesign/blob/master/LICENSE --><path fill="currentColor" d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93zM6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37z"/></svg>
            </a>
            <a
              href="https://www.youtube.com/c/Outtalent/featured"
              class="social-btn"
              aria-label="YouTube"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><!-- Icon from Material Design Icons by Pictogrammers - https://github.com/Templarian/MaterialDesign/blob/master/LICENSE --><path fill="currentColor" d="m10 15l5.19-3L10 9zm11.56-7.83c.13.47.22 1.1.28 1.9c.07.8.1 1.49.1 2.09L22 12c0 2.19-.16 3.8-.44 4.83c-.25.9-.83 1.48-1.73 1.73c-.47.13-1.33.22-2.65.28c-1.3.07-2.49.1-3.59.1L12 19c-4.19 0-6.8-.16-7.83-.44c-.9-.25-1.48-.83-1.73-1.73c-.13-.47-.22-1.1-.28-1.9c-.07-.8-.1-1.49-.1-2.09L2 12c0-2.19.16-3.8.44-4.83c.25-.9.83-1.48 1.73-1.73c.47-.13 1.33-.22 2.65-.28c1.3-.07 2.49-.1 3.59-.1L12 5c4.19 0 6.8.16 7.83.44c.9.25 1.48.83 1.73 1.73"/></svg>
            </a>
          </div>
        </div>

        <!-- Programs -->
        <div>
          <a
            href="https://outtalent.com/tuition"
            class="block text-xs font-semibold text-gray-900 tracking-wide mb-5 hover:text-[#1a66e6] transition-colors"
          >
            Programs
          </a>
          <ul class="space-y-3.5 list-none p-0 m-0">
            <li>
              <a
                href="https://outtalent.com/#program"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Curriculum
              </a>
            </li>
            <li>
              <a
                href="https://outtalent.com/#tuition"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Tuition
              </a>
            </li>
            <li>
              <a
                href="https://outtalent.com/#mentors"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Mentors
              </a>
            </li>
            <li>
              <a
                href="https://docs.outtalent.com/"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Docs
              </a>
            </li>
          </ul>
        </div>

        <!-- Company -->
        <div>
          <h4 class="text-xs font-semibold text-gray-900 tracking-wide mb-5">Company</h4>
          <ul class="space-y-3.5 list-none p-0 m-0">
            <li>
              <a
                href="https://outtalent.com/company"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                About Us
              </a>
            </li>
            <li>
              <a
                href="https://outtalent.com/results"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Outcomes
              </a>
            </li>
            <li>
              <a href="mailto:team@outtalent.com" class="footer-link">Contact</a>
            </li>
          </ul>
        </div>

        <!-- Explore -->
        <div>
          <h4 class="text-xs font-semibold text-gray-900 tracking-wide mb-5">Explore</h4>
          <ul class="space-y-3.5 list-none p-0 m-0">
            <li>
              <a
                href="https://outtalent.com/results"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Results
              </a>
            </li>
            <li>
              <a
                href="https://outtalent.com/mentors"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Mentors
              </a>
            </li>
            <li>
              <a
                href="https://outtalent.com/community"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Community
              </a>
            </li>
            <li>
              <a
                href="https://outtalent.com/company"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Company
              </a>
            </li>
            <li>
              <a
                href="https://outtalent.com/tuition"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Tuition
              </a>
            </li>
            <li>
              <a
                href="https://outtalent.notion.site/Join-Outtalent-201163e258c9465991e4532b22eb9c9e?pvs=74"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Careers
              </a>
            </li>
          </ul>
        </div>

        <!-- Legal -->
        <div>
          <h4 class="text-xs font-semibold text-gray-900 tracking-wide mb-5">Legal</h4>
          <ul class="space-y-3.5 list-none p-0 m-0">
            <li>
              <router-link to="/privacy-policy" class="footer-link">Privacy Policy</router-link>
            </li>
            <li>
              <router-link to="/terms-and-conditions" class="footer-link">
                Terms of Service
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Account -->
        <div>
          <h4 class="text-xs font-semibold text-gray-900 tracking-wide mb-5">Account</h4>
          <ul class="space-y-3.5 list-none p-0 m-0">
            <li>
              <router-link to="/login" class="footer-link">Sign in</router-link>
            </li>
            <li>
              <router-link to="/register" class="footer-link">Create account</router-link>
            </li>
          </ul>
        </div>
      </div>

      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-8 border-t border-gray-100"
      >
        <span class="text-xs text-gray-400">
          © {{ new Date().getFullYear() }} TrackTalent. All rights reserved.
        </span>
        <a
          href="https://outtalent.com"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs text-gray-400 hover:text-[#1a66e6] transition-colors"
        >
          A product of Outtalent
        </a>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
</script>

<style scoped>
.social-btn {
  @apply w-10 h-10 inline-flex items-center justify-center rounded-[10px]
    bg-gray-100 text-gray-500 transition-all duration-200 ease-out
    hover:bg-[#1a66e6] hover:text-white;
}

.footer-link {
  @apply text-sm text-gray-600 transition-colors duration-200 hover:text-[#1a66e6];
}
</style>
